'use client'

import dynamic from 'next/dynamic'
import { Controller, ControllerRenderProps } from 'react-hook-form'

import { TCommonFields } from '../../types'
import { FieldRow, getFieldRules } from '../field-row'
import { useFieldState } from '../hooks'

const Editor = dynamic(() => import('@tinymce/tinymce-react').then(mod => mod.Editor), {
    ssr: false,
})

export type TEditorField<T> = TCommonFields<T> & {
    type: 'editor'
    default?: string
}

type TEditorFieldProps<T> = {
    field: ControllerRenderProps<any, string>
    field_: TEditorField<T>
    disabled?: boolean
}

export const EditorField = <T,>({
    field: { ref, onBlur, onChange, disabled: fieldDisabled, ...rest },
    field_,
    disabled,
}: TEditorFieldProps<T>) => (
    <div ref={ref}>
        <Editor
            // the label points at the form field name, which differs from `field_.name` with transformName
            id={rest.name}
            {...rest}
            disabled={disabled ?? fieldDisabled ?? false}
            onEditorChange={value => {
                onChange(value)
                field_.onChange?.(value)
            }}
            initialValue={field_.default}
            tinymceScriptSrc="/js/tinymce/tinymce.min.js"
            init={{
                height: '480',
                plugins:
                    'advlist anchor autolink autosave charmap code codesample directionality emoticons fullscreen help image importcss insertdatetime link lists media nonbreaking pagebreak preview quickbars save searchreplace table visualblocks visualchars',
                menubar: 'file edit view insert format tools table help',
                toolbar:
                    'undo redo | bold italic underline strikethrough | fontfamily fontsize blocks | alignleft aligncenter alignright alignjustify | outdent indent |  numlist bullist | forecolor backcolor removeformat | pagebreak | charmap emoticons | fullscreen  preview save print | insertfile image media template link anchor codesample | ltr rtl',
                toolbar_sticky: true,
                toolbar_mode: 'sliding',
                contextmenu: 'link image table',
                language: 'cs',
                content_style: '* { margin:0; padding: 0; box-sizing: border-box; }',
                entity_encoding: 'raw',
            }}
            onInit={() => {
                const menu = document.querySelector('.tox.tox-silver-sink.tox-tinymce-aux')
                if (menu)
                    document.querySelector('[role="dialog"]')?.appendChild(menu)
            }}
        />
    </div>
)

type TProps<T> = {
    fieldName: string
    labelWidth?: string
    rowField: TEditorField<T>
}

const EditorFieldDefault = <T,>({ fieldName, labelWidth, rowField }: TProps<T>) => {
    const { disabled, required } = useFieldState(rowField)
    const noLabel = rowField.label == ''

    return (
        <FieldRow
            fieldName={fieldName}
            label={rowField.label}
            required={required}
            labelWidth={labelWidth}
            className={noLabel ? 'form-no-label' : undefined}
            hideLabel={noLabel}
        >
            <Controller
                name={fieldName}
                rules={getFieldRules(rowField, required)}
                render={({ field }) => <EditorField field={field} field_={rowField} disabled={disabled} />}
            />
        </FieldRow>
    )
}

export default EditorFieldDefault
